
// Import React polyfill first to ensure proper initialization
import './react-polyfill';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import App from './App.tsx';
import './index.css';
import "@/services/performanceOptimization";

const PUBLISHABLE_KEY_DEV = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_DEV;
const PUBLISHABLE_KEY_PROD = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_PROD;
const CLERK_FRONTEND_API_PROD = import.meta.env.VITE_CLERK_FRONTEND_API_PROD;

// Only use production keys for the exact production domain 
// Everything else (localhost, preview domains, staging, etc.) uses development keys
const isProduction = window.location.hostname.endsWith('.calone.com');

// Select the appropriate keys based on environment
const PUBLISHABLE_KEY = isProduction ? PUBLISHABLE_KEY_PROD : PUBLISHABLE_KEY_DEV;
const CLERK_FRONTEND_API = isProduction ? CLERK_FRONTEND_API_PROD : undefined;

// Import thumbnail utilities for development
if (!isProduction) {
  import('./utils/generateThumbnails').then((module) => {
    // Thumbnail utilities loaded silently
  }).catch(() => {
    // Ignore errors in case the file doesn't exist
  });
}

if (!PUBLISHABLE_KEY) {
  const env = isProduction ? 'production' : 'development';
  throw new Error(`Missing Clerk Publishable Key for ${env} environment`);
}

// Ensure React is fully loaded before proceeding
if (typeof React === 'undefined') {
  throw new Error('React is not loaded properly');
}

// Ensure DOM is ready
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Add error handling for the root render with proper error boundaries
try {
  const root = createRoot(rootElement);
  
  root.render(
    <React.StrictMode>
      <ClerkProvider 
        publishableKey={PUBLISHABLE_KEY} 
        {...(CLERK_FRONTEND_API && { frontendApi: CLERK_FRONTEND_API })}
        afterSignOutUrl="/"
        appearance={{
          layout: {
            logoImageUrl: "https://v1.calone.com/static/graphics/CalOne_Events_31_Logo.png",
            // logoLinkUrl: "https://events.calone.com",
            logoPlacement: 'inside',
            socialButtonsPlacement: 'top',
            socialButtonsVariant: 'blockButton',
            showOptionalFields: true,
            animations: true,
            shimmer: true,
          },
          variables: {
            colorPrimary: "#669933",
            colorText: "#222",
          },
        }}
      >
        <App />
      </ClerkProvider>
    </React.StrictMode>
  );
} catch (error) {
  console.error('Failed to initialize app:', error);
  
  // Fallback error display using vanilla DOM
  rootElement.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: system-ui;">
      <div style="text-align: center;">
        <h1 style="color: #dc2626; margin-bottom: 1rem;">Failed to load application</h1>
        <p style="color: #6b7280; margin-bottom: 1rem;">Please try refreshing the page</p>
        <button onclick="window.location.reload()" style="background: #3b82f6; color: white; padding: 0.5rem 1rem; border: none; border-radius: 0.375rem; cursor: pointer;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
}
